import React, { useState, useRef, useEffect } from 'react';
import './Header.css';
import { useLanguage } from '../../context/LanguageContext';
import { getTranslation } from '../../i18n/translations';

const Header = ({ onMenuClick, onNavigateToDeposit, onNavigateToWithdraw, isAuthenticated, user, onLogout, onNavigateToLogin, onOpenChat }) => {
  const { language, changeLanguage } = useLanguage();
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const popoverRef = useRef(null);
  const buttonRef = useRef(null);
  const langDropdownRef = useRef(null);
  const langButtonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        buttonRef.current &&
        !popoverRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setPopoverOpen(false);
      }
      
      if (
        langDropdownRef.current &&
        langButtonRef.current &&
        !langDropdownRef.current.contains(event.target) &&
        !langButtonRef.current.contains(event.target)
      ) {
        setLangDropdownOpen(false);
      }
    };

    if (popoverOpen || langDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popoverOpen, langDropdownOpen]);

  const handleDeposit = () => {
    setPopoverOpen(false);
    if (onNavigateToDeposit) {
      onNavigateToDeposit(null); // Navigate to deposit page without pre-selecting a currency
    }
  };

  const handleWithdraw = () => {
    setPopoverOpen(false);
    if (onNavigateToWithdraw) {
      onNavigateToWithdraw();
    }
  };

  const handleLanguageChange = (lang) => {
    changeLanguage(lang);
    setLangDropdownOpen(false);
  };

  // Flag components
  const USFlag = () => (
    <svg width="20" height="15" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Red stripes */}
      <rect width="20" height="1.15" fill="#B22234"/>
      <rect y="2.31" width="20" height="1.15" fill="#B22234"/>
      <rect y="4.62" width="20" height="1.15" fill="#B22234"/>
      <rect y="6.92" width="20" height="1.15" fill="#B22234"/>
      <rect y="9.23" width="20" height="1.15" fill="#B22234"/>
      <rect y="11.54" width="20" height="1.15" fill="#B22234"/>
      <rect y="13.85" width="20" height="1.15" fill="#B22234"/>
      {/* White stripes */}
      <rect y="1.15" width="20" height="1.16" fill="#FFF"/>
      <rect y="3.46" width="20" height="1.16" fill="#FFF"/>
      <rect y="5.77" width="20" height="1.15" fill="#FFF"/>
      <rect y="8.08" width="20" height="1.15" fill="#FFF"/>
      <rect y="10.38" width="20" height="1.16" fill="#FFF"/>
      <rect y="12.69" width="20" height="1.16" fill="#FFF"/>
      {/* Blue canton */}
      <rect width="8" height="6.92" fill="#3C3B6E"/>
      {/* Stars */}
      <circle cx="1" cy="1" r="0.4" fill="#FFF"/>
      <circle cx="2.5" cy="1" r="0.4" fill="#FFF"/>
      <circle cx="4" cy="1" r="0.4" fill="#FFF"/>
      <circle cx="5.5" cy="1" r="0.4" fill="#FFF"/>
      <circle cx="7" cy="1" r="0.4" fill="#FFF"/>
      <circle cx="1.75" cy="2" r="0.4" fill="#FFF"/>
      <circle cx="3.25" cy="2" r="0.4" fill="#FFF"/>
      <circle cx="4.75" cy="2" r="0.4" fill="#FFF"/>
      <circle cx="6.25" cy="2" r="0.4" fill="#FFF"/>
      <circle cx="1" cy="3" r="0.4" fill="#FFF"/>
      <circle cx="2.5" cy="3" r="0.4" fill="#FFF"/>
      <circle cx="4" cy="3" r="0.4" fill="#FFF"/>
      <circle cx="5.5" cy="3" r="0.4" fill="#FFF"/>
      <circle cx="7" cy="3" r="0.4" fill="#FFF"/>
      <circle cx="1.75" cy="4" r="0.4" fill="#FFF"/>
      <circle cx="3.25" cy="4" r="0.4" fill="#FFF"/>
      <circle cx="4.75" cy="4" r="0.4" fill="#FFF"/>
      <circle cx="6.25" cy="4" r="0.4" fill="#FFF"/>
      <circle cx="1" cy="5" r="0.4" fill="#FFF"/>
      <circle cx="2.5" cy="5" r="0.4" fill="#FFF"/>
      <circle cx="4" cy="5" r="0.4" fill="#FFF"/>
      <circle cx="5.5" cy="5" r="0.4" fill="#FFF"/>
      <circle cx="7" cy="5" r="0.4" fill="#FFF"/>
      <circle cx="1.75" cy="6" r="0.4" fill="#FFF"/>
      <circle cx="3.25" cy="6" r="0.4" fill="#FFF"/>
      <circle cx="4.75" cy="6" r="0.4" fill="#FFF"/>
      <circle cx="6.25" cy="6" r="0.4" fill="#FFF"/>
    </svg>
  );

  const RussiaFlag = () => (
    <svg width="20" height="15" viewBox="0 0 20 15" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect width="20" height="5" fill="#FFF"/>
      <rect y="5" width="20" height="5" fill="#0039A6"/>
      <rect y="10" width="20" height="5" fill="#D52B1E"/>
    </svg>
  );

  return (
    <header className="header">
      <div className="header__left-block">
        <button 
          className="header__menu-btn" 
          onClick={onMenuClick}
          aria-label="Menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 12H21" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 6H21" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M3 18H21" stroke="#344054" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <a href="/" className="header__logo-link">
          <img src="/Logo.svg" className="header__logo" alt="FX-Pool logo" />
        </a>
        <div className="header-rate">
          <div className="header-rate__item">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              className="header-rate__img"
              alt="icon"
            >
              <g clipPath="url(#clip0_272_22442)">
                <path
                  d="M24 12C24 14.3734 23.2962 16.6935 21.9776 18.6668C20.6591 20.6402 18.7849 22.1783 16.5922 23.0866C14.3995 23.9948 11.9867 24.2324 9.65892 23.7694C7.33115 23.3064 5.19295 22.1635 3.51472 20.4853C1.83649 18.8071 0.693605 16.6689 0.230582 14.3411C-0.232441 12.0133 0.00519936 9.60051 0.913451 7.4078C1.8217 5.21509 3.35977 3.34094 5.33316 2.02236C7.30655 0.703788 9.62663 0 12 0C15.1826 0 18.2348 1.26428 20.4853 3.51472C22.7357 5.76516 24 8.8174 24 12Z"
                  fill="#F7931A"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M6.713 6.60268L9.568 6.60229L9.56874 4.22359L10.9975 4.22813L10.9954 6.50894L12.16 6.50973L12.157 4.20545L13.6092 4.20484L13.6048 6.52353C13.6048 6.52353 15.9489 6.42059 16.9517 8.08089C17.9545 9.74119 16.5429 11.1325 16.0383 11.3061C16.0383 11.3061 18.262 11.8498 18.1565 14.1199C18.051 16.39 16.107 17.3119 13.8279 17.4087L13.8449 19.8075L12.3927 19.8081L12.3966 17.441L11.2432 17.436L11.2436 19.8189L9.80222 19.8178L9.80571 17.4451L6.87362 17.4402L7.15279 15.7255C7.15279 15.7255 7.9806 15.7296 8.29112 15.7209C8.60164 15.7123 8.70789 15.3585 8.70876 15.033C8.70964 14.7075 8.60013 9.34629 8.60613 8.90083C8.61213 8.45537 8.47804 8.14458 7.95283 8.14865C7.42762 8.15272 6.70619 8.13257 6.70619 8.13257L6.713 6.60268ZM11.1848 12.5434L11.2329 15.6736C11.2329 15.6736 15.2442 16.0139 15.2143 14.0308C15.1844 12.0477 11.1848 12.5434 11.1848 12.5434ZM11.1514 11.0615L11.1553 8.18052C11.1553 8.18052 14.4372 7.90197 14.4522 9.50056C14.4672 11.0992 12.4674 11.0206 11.1514 11.0615Z"
                  fill="white"
                />
              </g>
              <defs>
                <clipPath id="clip0_272_22442">
                  <rect width="24" height="24" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <div className="header-rate__block">
              <h3 className="header-rate__name">BTC</h3>
              <div className="header-rate__price-block">
                <button
                  ref={buttonRef}
                  className="header-rate__price-button"
                  onClick={() => setPopoverOpen(!popoverOpen)}
                >
                  <p className="header-rate__price">$90,707.00</p>
                  <svg
                    className={`header-rate__dropdown-icon ${popoverOpen ? 'open' : ''}`}
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 4.5L6 7.5L9 4.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {popoverOpen && (
                  <div ref={popoverRef} className="header-rate__popover">
                    <button
                      className="header-rate__popover-item"
                      onClick={handleDeposit}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 5v14M5 12h14" />
                      </svg>
                      <span>{getTranslation('deposit', language)}</span>
                    </button>
                    <button
                      className="header-rate__popover-item"
                      onClick={handleWithdraw}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14" />
                      </svg>
                      <span>{getTranslation('withdraw', language)}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="header__right-block">
        <div className="header-icon">
          <button className="header__icon header__icon--message" onClick={onOpenChat} title="Open Chat">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 22 22"
              fill="none"
              className="header-icon__svg"
            >
              <path
                d="M2.64941 10.4134C2.27646 12.3763 2.60726 14.4054 3.57954 16.1456L2.85332 18.7106C2.79562 18.9095 2.79224 19.1202 2.84356 19.3208C2.89509 19.5222 2.99985 19.7061 3.14688 19.8531C3.2939 20.0001 3.47777 20.1049 3.6792 20.1564C3.87983 20.2078 4.09054 20.2044 4.28941 20.1467L6.85435 19.4205C8.59458 20.3927 10.6237 20.7235 12.5866 20.3506C14.6208 19.9641 16.4439 18.8484 17.7138 17.213C18.9836 15.5775 19.6129 13.5348 19.4833 11.4683C19.3538 9.40185 18.4745 7.45369 17.0104 5.9896C15.5463 4.5255 13.5981 3.64616 11.5317 3.51665C9.46517 3.38715 7.42246 4.01638 5.78703 5.28623C4.1516 6.55608 3.03591 8.37921 2.64941 10.4134Z"
                stroke="#101828"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 11H14"
                stroke="#101828"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M8 13H14"
                stroke="#101828"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          {isAuthenticated ? (
            <>
              <a href="/general-settings/settings" className="header__icon-link">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="19"
                    height="20"
                    viewBox="0 0 19 20"
                    fill="none"
                    className="header-icon__svg"
                  >
                    <g clipPath="url(#clip0_5402_24128)">
                      <path
                        d="M0 5.06C0 3.46385 0 2.66578 0.310631 2.05613C0.583871 1.51987 1.01987 1.08387 1.55613 0.810631C2.16578 0.5 2.96385 0.5 4.56 0.5H14.44C16.0361 0.5 16.8342 0.5 17.4439 0.810631C17.9801 1.08387 18.4161 1.51987 18.6894 2.05613C19 2.66578 19 3.46385 19 5.06V14.94C19 16.5361 19 17.3342 18.6894 17.9439C18.4161 18.4801 17.9801 18.9161 17.4439 19.1894C17.3362 19.2442 17.2227 19.2894 17.1 19.3266C16.5275 19.5 15.7543 19.5 14.44 19.5H4.56C3.24567 19.5 2.47247 19.5 1.90002 19.3266C1.77728 19.2894 1.66377 19.2442 1.55613 19.1894C1.01987 18.9161 0.583871 18.4801 0.310631 17.9439C0 17.3342 0 16.5361 0 14.94V5.06Z"
                        fill="#101828"
                        stroke="#101828"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M9.5 10.5C11.433 10.5 13 8.93311 13 7C13 5.06689 11.433 3.5 9.5 3.5C7.56701 3.5 6 5.06689 6 7C6 8.93311 7.56701 10.5 9.5 10.5ZM9.5 9.5C10.8807 9.5 12 8.38062 12 7C12 5.61938 10.8807 4.5 9.5 4.5C8.11929 4.5 7 5.61938 7 7C7 8.38062 8.11929 9.5 9.5 9.5Z"
                        fill="white"
                      />
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7 11.5C4.79086 11.5 3 13.2909 3 15.5C3 16.0523 3.44772 16.5 4 16.5H15C15.5523 16.5 16 16.0523 16 15.5C16 13.2909 14.2091 11.5 12 11.5H7ZM6.92857 12.5C5.27172 12.5 3.92857 13.8431 3.92857 15.5H15.0714C15.0714 13.8431 13.7283 12.5 12.0714 12.5H6.92857Z"
                        fill="white"
                      />
                    </g>
                    <defs>
                      <clipPath id="clip0_5402_24128">
                        <rect y="0.5" width="19" height="19" rx="4" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                <span>{user?.email || getTranslation('user', language)}</span>
              </a>
              <button 
                className="header-logout-btn gray" 
                type="button"
                onClick={onLogout}
              >
                {getTranslation('logout', language)}
              </button>
            </>
          ) : (
            <button 
              className="header-login-btn primary" 
              type="button"
              onClick={onNavigateToLogin}
            >
              {getTranslation('login', language)}
            </button>
          )}
          <div className="language-dropdown">
              <button 
                ref={langButtonRef}
                className="gray dropdown-button language-button" 
                type="button"
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
              >
                <span className="language-button-content">
                  {language === 'en' ? <USFlag /> : <RussiaFlag />}
                  <span className="language-code">{language.toUpperCase()}</span>
                </span>
                <svg
                  className={`language-dropdown-icon ${langDropdownOpen ? 'open' : ''}`}
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 4.5L6 7.5L9 4.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {langDropdownOpen && (
                <div ref={langDropdownRef} className="language-dropdown-menu">
                  <button
                    className={`language-dropdown-item ${language === 'en' ? 'active' : ''}`}
                    onClick={() => handleLanguageChange('en')}
                  >
                    <USFlag />
                    <span>English (US)</span>
                  </button>
                  <button
                    className={`language-dropdown-item ${language === 'ru' ? 'active' : ''}`}
                    onClick={() => handleLanguageChange('ru')}
                  >
                    <RussiaFlag />
                    <span>Русский</span>
                  </button>
                </div>
              )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;

